import React, { FC } from 'react';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { MAX_SALARY, MIN_SALARY } from '@src/containers/Source/constants/salaryRange';
import { SelectedPlace } from '@src/containers/Source/interfaces/place.interface';
import { GroupedTalentDictionaryKey } from '@src/containers/Source/constants/groupedTalentDictionary/enums/groupedTalentDictionaryKey.enum';
import { BackButton } from '../BackButton';
import './styles.scss';

export const SearchAndFilterHeader: FC = () => {
  const { queryParams } = useQueryParams<QueryParams>();

  const isSavedTalentsPage = queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS;

  const getFilterCount = (
    specialitiesLength: number,
    senioritiesLength: number,
    parsedMinSalaryRange: number,
    parsedMaxSalaryRange: number,
  ): number => {
    let count = 0;

    if (specialitiesLength > 1) {
      count += specialitiesLength - 1;
    }

    if (senioritiesLength) {
      count += senioritiesLength;
    }

    if (parsedMinSalaryRange !== MIN_SALARY || parsedMaxSalaryRange !== MAX_SALARY) {
      count += 1;
    }

    return count;
  };

  const getLocationText = (place?: SelectedPlace, isRemote?: boolean, isRelocation?: boolean): string | undefined => {
    const result = [];

    if (place) {
      result.push(place.name);
    }

    if (isRemote) {
      result.push('Remote');
    }

    if (isRelocation) {
      result.push('Relocation');
    }

    if (result.length === 0) {
      return undefined;
    }

    if (result.length === 1) {
      return `${result[0]}`;
    }

    return `${result[0]} and ${result.length - 1} more`;
  };

  const getTitle = (queryParams: QueryParams) => {
    const {
      pageTitle,
      searchPatterns = [],
      place,
      isRelocation,
      isRemote,
      seniorities = [],
      specialities = [],
      salaryRange = [MIN_SALARY.toString(), MAX_SALARY.toString()],
    } = queryParams;

    const parsedSalaryRange = salaryRange.map(salary => parseFloat(salary));

    if (pageTitle) {
      return pageTitle;
    }

    if (searchPatterns?.length) {
      return 'Search';
    }

    if (queryParams.specialities?.length) {
      const count = getFilterCount(specialities.length, seniorities.length, parsedSalaryRange[0], parsedSalaryRange[1]);

      const locationText = getLocationText(place, !!isRemote, !!isRelocation);

      if (count > 0 && locationText) {
        return `${specialities[0].name}, +${count} in ${locationText}`;
      }

      if (count > 0) {
        return `${specialities[0].name}, +${count}`;
      }

      if (locationText) {
        return `${specialities[0].name} in ${locationText}`;
      }

      return specialities[0].name;
    }

    return 'Sourcing';
  };

  return (
    <div className="talent-pipeline-search-header">
      {!isSavedTalentsPage ? (
        <div className="talent-pipeline-search-header__back">
          <BackButton />
        </div>
      ) : null}

      <div className="talent-pipeline-search-header__title">{getTitle(queryParams)}</div>
    </div>
  );
};
